import {
  FaCheck,
  FaTimes,
  FaClock,
  FaShieldAlt,
  FaKey,
  FaPlus,
  FaTabletAlt,
  FaSyncAlt,
  FaCheckCircle,
} from "react-icons/fa";

/**
 * Tek bir bekleyen istemci karti — bir tempClientId icin BootstrapRequest +
 * PendingDevice fazlarinin progress timeline'ini gosterir.
 *
 * Faz cizimi (4 step):
 *   1. Baglanti Istendi   — BootstrapRequest olusturuldu
 *   2. Baglanti Onaylandi — admin BootstrapRequest'i onayladi (cred verildi)
 *   3. Mod Secildi        — cihaz install/screen_replacement/migration sec
 *   4. Cihaz Atandi       — admin mod onayladi, payload cihaza gitti
 */

const MODE_META = {
  new_install: {
    label: "Yeni Kurulum",
    Icon: FaPlus,
    classes: "bg-blue-50 text-blue-700 border-blue-200",
  },
  screen_replacement: {
    label: "Ekran Değişimi",
    Icon: FaTabletAlt,
    classes: "bg-amber-50 text-amber-700 border-amber-200",
  },
  migration: {
    label: "Yükseltme",
    Icon: FaSyncAlt,
    classes: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
};

const STEP_STATE = {
  done: {
    bg: "bg-emerald-500",
    ring: "ring-emerald-200",
    text: "text-emerald-700",
    line: "bg-emerald-300",
    Icon: FaCheck,
  },
  current: {
    bg: "bg-amber-500",
    ring: "ring-amber-200",
    text: "text-amber-700",
    line: "bg-gray-200",
    Icon: FaClock,
  },
  pending: {
    bg: "bg-gray-300",
    ring: "ring-gray-100",
    text: "text-gray-500",
    line: "bg-gray-200",
    Icon: FaClock,
  },
  rejected: {
    bg: "bg-red-500",
    ring: "ring-red-200",
    text: "text-red-700",
    line: "bg-gray-200",
    Icon: FaTimes,
  },
  skipped: {
    bg: "bg-gray-200",
    ring: "ring-gray-100",
    text: "text-gray-400",
    line: "bg-gray-200",
    Icon: FaCheck,
  },
};

function timeAgo(dateStr) {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  if (diff < 60_000) return "az önce";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} dk önce`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} sa önce`;
  return `${Math.floor(diff / 86_400_000)} gün önce`;
}

/**
 * Bootstrap + PendingDevice doc'larindan 4 adimli step listesi turetir.
 * Etiketler SABIT — durumla degismez (kullanici talebi: "her zaman tum
 * adimlari goster gelince basarili diyorsun"). Sadece state degisir
 * (done/current/pending/rejected). Boylece kart yuksekligi tum fazlarda
 * tutarli.
 *
 * Adimlar:
 *   1. Bağlantı İstendi   — bootstrap kaydi olusunca done
 *   2. Bağlantı Onaylandı — bootstrap approved/credentials_issued done
 *   3. Mod Seçimi         — pending (install request) kaydi olusunca done
 *   4. Cihaz Atandı       — pending.status === "assigned" done
 *
 * Her step: { id, label, state, ts, detail }
 */
export function buildSteps({ bootstrap, pending }) {
  // STEP 1 — Baglanti istegi
  const step1 = bootstrap
    ? {
        id: "bootstrap_request",
        label: "Bağlantı İstendi",
        state: "done",
        ts: bootstrap.createdAt,
      }
    : {
        id: "bootstrap_request",
        label: "Bağlantı İstendi",
        state: "skipped",
        ts: null,
        detail: "(MQTT migration)",
      };

  // STEP 2 — Baglanti onayi (etiket sabit: "Bağlantı Onaylandı")
  let step2;
  if (bootstrap?.status === "rejected") {
    step2 = {
      id: "bootstrap_approved",
      label: "Bağlantı Onaylandı",
      state: "rejected",
      ts: bootstrap.rejectedAt || bootstrap.updatedAt,
      detail: bootstrap.rejectedReason,
    };
  } else if (
    bootstrap?.status === "credentials_issued" ||
    bootstrap?.status === "approved"
  ) {
    step2 = {
      id: "bootstrap_approved",
      label: "Bağlantı Onaylandı",
      state: "done",
      ts:
        bootstrap.credentialsIssuedAt || bootstrap.approvedAt || null,
    };
  } else if (bootstrap?.status === "pending_approval") {
    step2 = {
      id: "bootstrap_approved",
      label: "Bağlantı Onaylandı",
      state: "current",
      ts: null,
    };
  } else {
    step2 = {
      id: "bootstrap_approved",
      label: "Bağlantı Onaylandı",
      state: "pending",
      ts: null,
    };
  }

  // STEP 3 — Mod secimi (etiket sabit: "Mod Seçimi")
  let step3;
  if (pending?.status === "rejected") {
    step3 = {
      id: "mode_request",
      label: "Mod Seçimi",
      state: "rejected",
      ts: pending.rejectedAt,
      detail: pending.rejectedReason,
      mode: pending.mode,
    };
  } else if (pending) {
    step3 = {
      id: "mode_request",
      label: "Mod Seçimi",
      state: "done",
      ts: pending.firstSeen || pending.createdAt,
      mode: pending.mode,
      detail: MODE_META[pending.mode]?.label || null,
    };
  } else if (
    bootstrap?.status === "credentials_issued" ||
    bootstrap?.status === "approved"
  ) {
    step3 = {
      id: "mode_request",
      label: "Mod Seçimi",
      state: "current",
      ts: null,
    };
  } else {
    step3 = {
      id: "mode_request",
      label: "Mod Seçimi",
      state: "pending",
      ts: null,
    };
  }

  // STEP 4 — Cihaz atamasi (etiket sabit: "Cihaz Atandı")
  let step4;
  if (pending?.status === "assigned") {
    step4 = {
      id: "mode_assigned",
      label: "Cihaz Atandı",
      state: "done",
      ts: pending.assignedAt,
      detail: pending.assignedSerialNo
        ? `Seri No: ${pending.assignedSerialNo}`
        : null,
    };
  } else if (pending?.status === "claimed") {
    step4 = {
      id: "mode_assigned",
      label: "Cihaz Atandı",
      state: "current",
      ts: pending.claimedAt,
    };
  } else if (pending?.status === "pending") {
    step4 = {
      id: "mode_assigned",
      label: "Cihaz Atandı",
      state: "current",
      ts: null,
    };
  } else if (pending?.status === "rejected") {
    step4 = {
      id: "mode_assigned",
      label: "Cihaz Atandı",
      state: "rejected",
      ts: null,
    };
  } else {
    step4 = {
      id: "mode_assigned",
      label: "Cihaz Atandı",
      state: "pending",
      ts: null,
    };
  }

  return [step1, step2, step3, step4];
}

export function derivePhase({ bootstrap, pending }) {
  if (pending?.status === "assigned") return "completed";
  if (pending?.status === "rejected") return "mode_rejected";
  if (pending?.status === "claimed") return "mode_claimed";
  if (pending?.status === "pending") return "mode_pending";
  if (bootstrap?.status === "rejected") return "bootstrap_rejected";
  if (bootstrap?.status === "credentials_issued") return "awaiting_mode";
  if (bootstrap?.status === "approved") return "awaiting_mode";
  if (bootstrap?.status === "pending_approval") return "bootstrap_pending";
  return "unknown";
}

const PHASE_BADGE = {
  bootstrap_pending: {
    label: "Bağlantı Onayı Bekliyor",
    color: "bg-amber-100 text-amber-700 border-amber-200",
  },
  bootstrap_rejected: {
    label: "Bağlantı Reddedildi",
    color: "bg-red-100 text-red-700 border-red-200",
  },
  awaiting_mode: {
    label: "Mod Bekleniyor",
    color: "bg-blue-100 text-blue-700 border-blue-200",
  },
  mode_pending: {
    label: "Onay Bekliyor",
    color: "bg-amber-100 text-amber-700 border-amber-200",
  },
  mode_claimed: {
    label: "İşleniyor",
    color: "bg-yellow-100 text-yellow-700 border-yellow-200",
  },
  mode_rejected: {
    label: "Reddedildi",
    color: "bg-red-100 text-red-700 border-red-200",
  },
  completed: {
    label: "Tamamlandı",
    color: "bg-emerald-100 text-emerald-700 border-emerald-200",
  },
  unknown: {
    label: "Bilinmiyor",
    color: "bg-gray-100 text-gray-700 border-gray-200",
  },
};

export default function PendingClientCard({
  client,
  onOpen,
  isNew,
}) {
  const { tempClientId, bootstrap, pending } = client;
  const steps = buildSteps({ bootstrap, pending });
  const phase = derivePhase({ bootstrap, pending });
  const badge = PHASE_BADGE[phase];
  // Doğrulama kodu öncelik sırası: pending (install request) > bootstrap.
  // Bootstrap aşamasındaki cihaz henüz pending kayıt oluşturmadı; bu durumda
  // bootstrap.code gösterilir. Install aşamasında pending.code önceliklidir
  // (cihaz install request'inde tazelenmiş yeni kod gönderiyor).
  const code = pending?.code || bootstrap?.code || null;
  const mode = pending?.mode;
  const modeMeta = mode ? MODE_META[mode] : null;
  const ModeIcon = modeMeta?.Icon;
  const isOnline = !!pending?.online;

  return (
    <div
      className={`h-full bg-white rounded-xl shadow-sm border ${
        isNew ? "border-amber-300 ring-2 ring-amber-100" : "border-input"
      } p-4 flex flex-col gap-3 transition`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${badge.color}`}
            >
              {badge.label}
            </span>
            {modeMeta && (
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${modeMeta.classes}`}
              >
                {ModeIcon && <ModeIcon className="w-2.5 h-2.5" />}
                {modeMeta.label}
              </span>
            )}
            {pending && (
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                  isOnline
                    ? "bg-green-100 text-green-700"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    isOnline ? "bg-green-500" : "bg-gray-400"
                  }`}
                />
                {isOnline ? "Online" : "Offline"}
              </span>
            )}
          </div>

          {/* Doğrulama Kodu — her kartta sabit gosterilir (kullanici talebi).
              Kod henuz uretilmedi/server kaydetmediyse "—" placeholder ile
              gosterilerek kart yuksekligi tutarli kalir. */}
          <div className="mt-2">
            <div className="text-[10px] uppercase tracking-wider text-mainSecond/60">
              Doğrulama Kodu
            </div>
            <div
              className={`font-mono font-bold text-2xl tracking-widest leading-tight ${
                code ? "text-main" : "text-mainSecond/30"
              }`}
            >
              {code || "——————"}
            </div>
            {/* Client ID — koda hemen altında belirgin gösterilir; karta
                (drawer'a) girmeden tempClientId okunabilsin (kullanıcı talebi). */}
            <div className="mt-1.5">
              <div className="text-[10px] uppercase tracking-wider text-mainSecond/60">
                Client ID
              </div>
              <div className="font-mono text-[11px] text-mainSecond/70 break-all leading-tight">
                {tempClientId}
              </div>
            </div>
          </div>
        </div>

        <div className="text-right shrink-0">
          {bootstrap?.serialNo || pending?.assignedSerialNo ? (
            <div className="text-[10px] text-mainSecond/60 uppercase tracking-wider">
              Seri No
            </div>
          ) : null}
          <div className="font-mono text-xs font-semibold text-mainSecond">
            {pending?.assignedSerialNo ||
              pending?.claimedSerialNo ||
              bootstrap?.serialNo ||
              ""}
          </div>
        </div>
      </div>

      {/* Timeline */}
      <ol className="flex flex-col gap-2 mt-1">
        {steps.map((step, idx) => {
          const visual = STEP_STATE[step.state] || STEP_STATE.pending;
          const StepIcon = visual.Icon;
          const isLast = idx === steps.length - 1;
          return (
            <li key={step.id} className="flex items-start gap-2.5 relative">
              <div className="flex flex-col items-center shrink-0">
                <div
                  className={`w-6 h-6 rounded-full ${visual.bg} ring-4 ${visual.ring} flex items-center justify-center text-white shrink-0`}
                >
                  <StepIcon className="w-2.5 h-2.5" />
                </div>
                {!isLast && (
                  <div className={`w-0.5 grow ${visual.line} mt-0.5`} style={{ minHeight: 12 }} />
                )}
              </div>
              <div className="flex-1 min-w-0 pb-0.5">
                <div className={`text-xs font-semibold ${visual.text}`}>
                  {step.label}
                </div>
                {step.detail && (
                  <div className="text-[11px] text-mainSecond/60 truncate">
                    {step.detail}
                  </div>
                )}
                {step.ts && (
                  <div className="text-[10px] text-mainSecond/50">
                    {timeAgo(step.ts)}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {/* Cihaz Detayları — her kartta sabit gosterilir (kullanici talebi:
          "bos olsa bile default deger ile doldur"). Bootstrap-only fazda
          pending yok → tum alanlar "—" gosterilir. Pending olusunca
          identity verisi gercek degerlerle doldurulur. */}
      {(() => {
        const identity = pending?.identity || {};
        const teaPots = identity.potCountTeaFilterCoffee || 0;
        const tcPots = identity.potCountTurkishCoffee || 0;
        const serComs = Array.isArray(identity.serComs) ? identity.serComs : [];
        const hwTea = pending?.hardwareVersionTeaFilterCoffee || "";
        const hwTc = pending?.hardwareVersionTurkishCoffee || "";
        return (
          <div className="border-t border-input pt-2 space-y-1.5">
            <div className="text-[10px] uppercase tracking-wider text-mainSecond/60 font-semibold">
              Cihaz Detayları
            </div>
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-mainSecond/60">Makine Adı</span>
              <span className="font-semibold text-mainSecond truncate ml-2 max-w-[60%]">
                {identity.machineName || "—"}
              </span>
            </div>
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-mainSecond/60">Göz Sayısı</span>
              <span className="font-semibold text-mainSecond">
                <span className="mr-2">
                  Çay/Filtre:{" "}
                  <span className="text-main">{teaPots || "—"}</span>
                </span>
                <span>
                  Türk: <span className="text-main">{tcPots || "—"}</span>
                </span>
              </span>
            </div>
            {/* Eski kart seti (OKC_ ön kayıt ID'si) tablet değil — seri port yok (#319). */}
            {!String(tempClientId || "").startsWith("OKC_") && (
              <div className="space-y-1">
                <div className="text-[10px] text-mainSecond/60">Portlar</div>
                {serComs.length > 0 ? (
                  serComs.map((s, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between text-[10px] font-mono bg-input/30 rounded px-2 py-1"
                    >
                      <span className="text-mainSecond/70">
                        {s.machineType || "—"}
                      </span>
                      <span className="text-mainSecond">
                        {s.serCom || "—"}
                        {s.baudRate ? (
                          <span className="text-mainSecond/50 ml-1.5">
                            @ {s.baudRate}
                          </span>
                        ) : null}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="text-[10px] font-mono bg-input/30 rounded px-2 py-1 text-mainSecond/50 text-center">
                    —
                  </div>
                )}
              </div>
            )}
            <div className="flex items-center justify-between text-[10px] text-mainSecond/50">
              <span>Donanım</span>
              <span className="font-mono">
                <span className="mr-2">T:{hwTea || "—"}</span>
                <span>C:{hwTc || "—"}</span>
              </span>
            </div>
          </div>
        );
      })()}

      {/* Esnek bosluk — kartlarin grid icinde ayni yukseklikte hizalanmasi
          icin alt butonu tabana yapistirir (icerik farklilasinca dahi).
          Client ID artik ust kisimda (Dogrulama Kodu altinda) gosteriliyor. */}
      <div className="flex-grow" />

      <button
        type="button"
        onClick={() => onOpen(client)}
        className="w-full px-3 py-2 rounded-lg bg-main text-new font-medium hover:bg-mainSecond transition text-sm flex items-center justify-center gap-2"
      >
        {phase === "completed" ? (
          <>
            <FaCheckCircle className="w-3 h-3" />
            Detay
          </>
        ) : phase === "bootstrap_pending" ? (
          <>
            <FaShieldAlt className="w-3 h-3" />
            Bağlantıyı Onayla
          </>
        ) : phase === "mode_pending" || phase === "mode_claimed" ? (
          <>
            <FaKey className="w-3 h-3" />
            Modu Onayla
          </>
        ) : (
          "İncele"
        )}
      </button>
    </div>
  );
}
